import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VisitorsAPI } from "../api/visitors";
import { filterArrayByString } from "../componets/helper";
import { DataGrid } from "@mui/x-data-grid";
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import GridCustomHeader from "../componets/AppLayout/GridCustomHeader";
import AppLayout from "../componets/AppLayout/AppLayout";
import VisibilityIcon from "../assets/SVGs/Login/VisibilityIcon";
import AddVisitorsModal from "../componets/Modal/AddVisitorsModal";
import { defaultEditFormState } from "../componets/constant";
import EditVisitorModal from "../componets/Modal/EditVisitorModal";
import dayjs from "dayjs";
import { useAppContext } from "../context/AppContext";
import { MembershipAPI } from "../api/membership";
import { DatePicker } from "@mui/x-date-pickers";
import {
  memberOptionGroup,
  memberOptionLabel,
  sortByName,
  sortMembersDepartedLast,
} from "../componets/helper";
import renderMemberOption from "../componets/MemberOption";
import MemberNameCell from "../componets/MemberNameCell";

const Visitors = () => {
  const { openSnack } = useAppContext();
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);
  const [members, setMembers] = useState([]);

  const handleSearch = (searchString) => {
    setSearchText(searchString);
  };

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("");
      setMembers(sortByName(response?.items || [], "first_name"));
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  const columns = [
    {
      field: "member_who_brought_visitor",
      headerName: "Brought by",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      valueGetter: (params) => `${params?.first_name} ${params?.last_name}`,
      renderCell: (params) => (
        <MemberNameCell member={params?.row?.member_who_brought_visitor} />
      ),

      editable: true,
      renderEditCell: (params) => {
        const handleChange = (_e, e) => {
          const newValue = e;
          params.api.setEditCellValue({
            id: params.id,
            field: "member_who_brought_visitor",
            value: newValue,
          });
        };
        return (
          <Autocomplete
            value={params?.value || null}
            onChange={handleChange}
            id="member_who_brought_visitor"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField {...params} className="login-textField" size="small" />
            )}
            className="login-textField"
            size="small"
          />
        );
      },
    },
    {
      field: "visitor_first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      editable: true,
    },
    {
      field: "visitor_last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      editable: true,
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      editable: true,
    },
    {
      field: "email_address",
      headerName: "Email Address",
      flex: 1,
      minWidth: isMobileTable ? 200 : undefined,
      editable: true,
    },
    {
      field: "date_visited",
      headerName: "Date Visited",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
      renderEditCell: (params) => {
        const handleChange = (event) => {
          const newValue = event;
          params.api.setEditCellValue({
            id: params.id,
            field: "date_visited",
            value: newValue,
          });
        };

        return (
          <DatePicker
            value={dayjs(params.value)}
            onChange={handleChange}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "date_visited",
                name: "date_visited",
                className: "login-textField",
                size: "small",
              },
            }}
          />
        );
      },
    },
    {
      field: "chair",
      headerName: "Chair",
      flex: 1,
      minWidth: isMobileTable ? 250 : undefined,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: `${params.row?.visitor_first_name} ${params?.row?.visitor_last_name} Visitor`,
            })
          }
        >
          <VisibilityIcon
            sx={{
              color: "#64748B",
            }}
          />
        </IconButton>
      ),
    },
  ];

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await VisitorsAPI.getVisitors());
    } catch (e) {
      console.log(e.response);
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isLoading && fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getRowsData = () => {
    const dummyRows = [...(data?.items || [])];

    return filterArrayByString(dummyRows || [], searchText);
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      await EditReferral(newRow);
      return newRow; // Return the updated row
    } catch (error) {
      console.error("Row update failed:", error);
      return oldRow; // Revert to the old row if the update fails
    }
  };

  const EditReferral = async (form) => {
    const formObj = {
      date_visited: dayjs(form?.date_visited)?.format("YYYY-MM-DD"),
      member_who_brought_visitor_id: form?.member_who_brought_visitor?.id,
      visitor_first_name: form?.visitor_first_name,
      visitor_last_name: form?.visitor_last_name,
      phone_number: form?.phone_number,
      email_address: form?.email_address,
      chair: form?.chair,
    };

    !formObj?.email_address && delete formObj?.email_address;

    try {
      await VisitorsAPI.updateVisitor(formObj, form?.id);
      openSnack("Visitor information updated successfully.", "success");
      fetch();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          width: "100%",
          padding: isMobileTable ? "8px" : "24px",
        }}
      >
        <GridCustomHeader
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          searchText={searchText}
          handleSearch={handleSearch}
          setOpenModal={setOpenModal}
          buttonText="Add New Visitor"
          headerName="Visitors"
        />
        <Stack
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              borderRadius: "8px",
            },
          }}
          className="customDataGridFigma"
        >
          <DataGrid
            rows={getRowsData() || []}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            processRowUpdate={processRowUpdate}
            pageSizeOptions={[10, 15, 20, 25]}
            checkboxSelection={false}
            rowSelection={false}
            rowHeight={48}
            columnHeaderHeight={40}
            getRowClassName={(params) => {
              const customClassName =
                params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
              return ` ${customClassName}`.trim();
            }}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "linear-progress",
              },
            }}
            localeText={{
              noRowsLabel: "No Visitors found.",
            }}
            disableVirtualization
          />
        </Stack>
      </Box>
      {openModal && (
        <AddVisitorsModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          setIsLoading={setIsLoading}
        />
      )}

      {openEditModal.open && (
        <EditVisitorModal
          open={openEditModal?.open}
          id={openEditModal?.id}
          handleClose={() => setOpenEditModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openEditModal?.title}
        />
      )}
    </AppLayout>
  );
};

export default Visitors;
